import { blockWire, planWire } from "./models.js";
import { parse } from "./ruleParser.js";
import { adapt, prepare } from "./trackSession.js";
import type { RunReading, WeightsReading } from "./types.js";
import { defaultTimeZone } from "./week.js";

export type * from "./types.js";
export type { TrainingWeek, TrainingDay } from "./trainingWeek.js";
export type { LabelReading, PreviewSession, ReadOptions } from "./week.js";
export {
  NOT_READ_MARK,
  READING_VERSION,
  expandLines,
  familyOf,
  isCurrentReading,
  mergePicks,
  picksFromLogs,
  picksIn,
  picksOf,
  publishedBlocks,
  readDefinition,
  readingMarks,
} from "./reading.js";
export type { DefinitionSource, Expanded, PublishedBlockWire, ReadDefinitionOptions } from "./reading.js";
export { fragments, isRun, isRunning, loadIn, readRunName } from "./trackSession.js";
export type { RunName } from "./trackSession.js";
export { blankDraft, copyWeek, newColumnID, nextTitle, normalizeDraft, sourceBefore, startFromPublished, startWeek } from "./start.js";
export type { StartSources } from "./start.js";
export { exerciseKey } from "./exerciseKey.js";
export { breaksPhrase, partsText, withoutBreaksPhrase } from "./models.js";
export { lineSpans } from "./lineSpans.js";
export type { LineSpan, SpanKind } from "./lineSpans.js";
export { applyLogs, applyRecords, blankItem, formRows, countText, itemRecords, EFFORT_WORDS, liftLines, parseCount, roundsRow, newRowID, readsSame, renderItem, renderRows, ROWS_VERSION, RowsError, RUN_KINDS, RUN_STARTS, SURFACES, validLogs, validRows } from "./rows/index.js";
export { displayLabel, isPrepLabel, labelKey, looksLikeLabel } from "./trainingWeek.js";
export {
  columnFor,
  columnsMatching,
  defaultTimeZone,
  draftFromPublished,
  isWeightsLabel,
  labelFamily,
  nextLabel,
  publishRows,
  readDraft,
  inheritedPicks,
  legacyPicks,
  ownPicks,
  readingsByLabel,
  readingsOf,
  referencedLabels,
  toTrainingWeek,
  withPartBlocks,
  withReadings,
} from "./week.js";

/**
 * `RuleParser.parse` on one definition's text, with any prep it names already written in by the
 * caller. `unread` is every line behind a `Couldn’t read` warning, verbatim.
 */
export function readWeights(
  text: string,
  knownExercises: string[] = [],
  options: { now?: Date; timeZone?: string } = {},
): WeightsReading {
  const result = parse(text, {
    knownExercises,
    now: (options.now ?? new Date()).getTime(),
    timeZone: options.timeZone ?? defaultTimeZone,
  });
  return {
    plan: planWire(result.plan),
    unread: result.unread,
    warnings: result.warnings,
    confidence: result.confidence,
    lines: result.lines,
  };
}

/**
 * A non-weights definition read as the phone logs it (DESIGN.md §9.3, §19.2.1): `TrackSession.prepare`,
 * the rules, prep and empty blocks dropped, then `TrackSession.adapt`. `groups` are the athlete's columns.
 */
export function readRun(lines: string[], groups: string[] = []): RunReading {
  const result = parse(prepare(lines, groups));
  const blocks = adapt(result.plan.blocks.filter((b) => !b.isPrep && b.exercises.length > 0));
  return { blocks: blocks.map(blockWire), unread: result.unread };
}
